import { FeatureName } from 'chaika-enums';

import type { Optional } from 'chaika-frontend-types/frontend-cms';
import type { EntryCdaCreationParams } from 'chaika-types';

import { CDA_NOT_AVAILABLE_ERROR_CODE, EgiszApi } from '@api/EgiszApi';
import { translate } from '@chaika/i18n/helper';
import { store } from '@store';
import { featureToggleService } from '@store/Configuration/featureToggleService';
import { getErrorMessage } from '@utils/getErrorMessage';

import type { SignData } from './SignData';

export async function prepareSignedCda(entryId: string, signData: SignData): Promise<Optional<EntryCdaCreationParams>> {
    if (!featureToggleService.isAvailableForAuthUser(FeatureName.CdaGeneration)) {
        return undefined;
    }

    const xml = await requestXml(entryId);
    if (!xml) {
        return undefined;
    }

    const signature = await signXml(xml, signData);

    return signature ? { xml, signature } : undefined;
}

async function requestXml(entryId: string): Promise<Optional<string>> {
    try {
        const { cdaXml } = await EgiszApi.generateCda(entryId);

        return cdaXml;
    } catch (error: unknown) {
        if (isCdaNotAvailable(error)) {
            return undefined;
        }

        const shouldRetry = await askToRetry(translate('cdaRequestFailed'), getErrorMessage(error));

        return shouldRetry ? requestXml(entryId) : undefined;
    }
}

async function signXml(xml: string, signData: SignData): Promise<Optional<string>> {
    const signature = await signData(xml);
    if (signature) {
        return signature;
    }

    const shouldRetry = await askToRetry(translate('cdaSigningFailed'), translate('failedToSignEntry'));

    return shouldRetry ? signXml(xml, signData) : undefined;
}

function isCdaNotAvailable(error: unknown): boolean {
    return error instanceof Error && error.name === CDA_NOT_AVAILABLE_ERROR_CODE;
}

function askToRetry(title: string, description: string): Promise<boolean> {
    const { app } = store;

    return new Promise(resolve => {
        const answer = (shouldRetry: boolean) => {
            app.showConfirmPopup(null);
            resolve(shouldRetry);
        };

        app.showConfirmPopup({
            title,
            description,
            confirmText: translate('tryAgain'),
            cancelText: translate('continueWithoutCda'),
            onConfirm: () => answer(true),
            onCancel: () => answer(false),
        });
    });
}
